import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus } from 'lucide-react';

const SERVICE_VALUES = [
  'Bath',
  'Haircut',
  'Nail Trim',
  'Ear Cleaning',
  'Brush',
  'De-shedding',
  'Teeth Brushing',
  'Anal Gland Expression',
] as const;
const SERVICE_KEYS = [
  'bath',
  'haircut',
  'nailTrim',
  'earCleaning',
  'brush',
  'deshedding',
  'teethBrushing',
  'analGlandExpression',
] as const;
const SERVICE_TYPE_KEYS = [
  'fullGrooming',
  'basicBathBrush',
  'nailTrimOnly',
  'desheddingTreatment',
  'puppyFirstGroom',
  'other',
] as const;
const SERVICE_TYPE_VALUES = [
  'Full Grooming Service',
  'Basic Bath & Brush',
  'Nail Trim Only',
  'De-shedding Treatment',
  'Puppy/Kitten First Groom',
  'Other',
] as const;

interface AddGroomingModalProps {
  onClose: () => void;
  onAdd: (record: {
    serviceType: string;
    date: string;
    groomerName: string;
    clinicName: string;
    coatCondition: string;
    behavioralObservations: string;
    groomerNotes: string;
    services: string[];
  }) => void;
}

export function AddGroomingModal({ onClose, onAdd }: AddGroomingModalProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    serviceType: '',
    date: '',
    groomerName: '',
    clinicName: '',
    coatCondition: '',
    behavioralObservations: '',
    groomerNotes: '',
    services: [] as string[],
  });

  const availableServices = SERVICE_VALUES.map((value, i) => ({
    value,
    label: t(`features.healthRecords.addGroomingModal.${SERVICE_KEYS[i]}`),
  }));

  const toggleService = (service: string) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter((s) => s !== service)
        : [...prev.services, service],
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd(formData);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col border dark:border-white/8">
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 dark:from-purple-700 dark:to-pink-700 text-white p-6 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div>
              <h2
                className="text-2xl mb-1"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {t('features.healthRecords.addGroomingModal.title')}
              </h2>
              <p
                className="text-sm opacity-90"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('features.healthRecords.addGroomingModal.subtitle')}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 min-h-0 overflow-auto p-6 bg-white dark:bg-[#16151D]"
        >
          <div className="space-y-4">
            {/* Service Type */}
            <div>
              <label
                className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('features.healthRecords.addGroomingModal.serviceType')}{' '}
                <span className="text-red-500 dark:text-red-400">*</span>
              </label>
              <select
                required
                value={formData.serviceType}
                onChange={(e) =>
                  setFormData({ ...formData, serviceType: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <option value="">
                  {t(
                    'features.healthRecords.addGroomingModal.selectServiceType'
                  )}
                </option>
                {SERVICE_TYPE_VALUES.map((val, i) => (
                  <option key={val} value={val}>
                    {t(
                      `features.healthRecords.addGroomingModal.${SERVICE_TYPE_KEYS[i]}`
                    )}
                  </option>
                ))}
              </select>
            </div>

            {/* Date */}
            <div>
              <label
                className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('features.healthRecords.addGroomingModal.serviceDate')}{' '}
                <span className="text-red-500 dark:text-red-400">*</span>
              </label>
              <input
                type="date"
                required
                value={formData.date}
                onChange={(e) =>
                  setFormData({ ...formData, date: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
                style={{ fontFamily: 'Inter, sans-serif' }}
              />
            </div>

            {/* Groomer & Clinic */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label
                  className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('features.healthRecords.addGroomingModal.groomerName')}{' '}
                  <span className="text-red-500 dark:text-red-400">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.groomerName}
                  onChange={(e) =>
                    setFormData({ ...formData, groomerName: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/40"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                  placeholder={t(
                    'features.healthRecords.addGroomingModal.groomerPlaceholder'
                  )}
                />
              </div>

              <div>
                <label
                  className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('features.healthRecords.addGroomingModal.salonClinicName')}{' '}
                  <span className="text-red-500 dark:text-red-400">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.clinicName}
                  onChange={(e) =>
                    setFormData({ ...formData, clinicName: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/40"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                  placeholder={t(
                    'features.healthRecords.addGroomingModal.salonPlaceholder'
                  )}
                />
              </div>
            </div>

            {/* Services Provided */}
            <div>
              <label
                className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('features.healthRecords.addGroomingModal.servicesProvided')}{' '}
                <span className="text-red-500 dark:text-red-400">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {availableServices.map(({ value, label }) => (
                  <label
                    key={value}
                    className="flex items-center gap-2 p-3 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={formData.services.includes(value)}
                      onChange={() => toggleService(value)}
                      className="w-4 h-4 text-purple-600 dark:text-purple-400"
                    />
                    <span
                      className="text-sm text-gray-900 dark:text-white"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {label}
                    </span>
                  </label>
                ))}
              </div>
              {formData.services.length === 0 && (
                <p
                  className="text-xs text-red-500 dark:text-red-400 mt-1"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t(
                    'features.healthRecords.addGroomingModal.selectOneService'
                  )}
                </p>
              )}
            </div>

            {/* Coat Condition */}
            <div>
              <label
                className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('features.healthRecords.addGroomingModal.coatCondition')}{' '}
                <span className="text-red-500 dark:text-red-400">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.coatCondition}
                onChange={(e) =>
                  setFormData({ ...formData, coatCondition: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/40"
                style={{ fontFamily: 'Inter, sans-serif' }}
                placeholder={t(
                  'features.healthRecords.addGroomingModal.coatPlaceholder'
                )}
              />
            </div>

            {/* Behavioral Observations */}
            <div>
              <label
                className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t(
                  'features.healthRecords.addGroomingModal.behavioralObservations'
                )}{' '}
                <span className="text-red-500 dark:text-red-400">*</span>
              </label>
              <textarea
                required
                value={formData.behavioralObservations}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    behavioralObservations: e.target.value,
                  })
                }
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/40"
                style={{ fontFamily: 'Inter, sans-serif' }}
                placeholder={t(
                  'features.healthRecords.addGroomingModal.behavioralPlaceholder'
                )}
              />
            </div>

            {/* Groomer Notes */}
            <div>
              <label
                className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('features.healthRecords.addGroomingModal.groomerNotes')}{' '}
                <span className="text-red-500 dark:text-red-400">*</span>
              </label>
              <textarea
                required
                value={formData.groomerNotes}
                onChange={(e) =>
                  setFormData({ ...formData, groomerNotes: e.target.value })
                }
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/40"
                style={{ fontFamily: 'Inter, sans-serif' }}
                placeholder={t(
                  'features.healthRecords.addGroomingModal.groomerNotesPlaceholder'
                )}
              />
            </div>

            {/* Info Notice */}
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-500/30 rounded-lg p-4">
              <p
                className="text-sm text-purple-800 dark:text-purple-200"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                ✂️ {t('features.healthRecords.addGroomingModal.groomingNotice')}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-white/8">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-gray-900 dark:text-white"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              {t('features.healthRecords.addGroomingModal.cancel')}
            </button>
            <button
              type="submit"
              disabled={formData.services.length === 0}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 dark:from-purple-700 dark:to-pink-700 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              <Plus className="w-5 h-5" />
              {t('features.healthRecords.addGroomingModal.addRecord')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
